// src/services/courierService.js
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import stateDistrictData from '../pages/StatenDistrict.json';

export const COURIERS_COLLECTION = 'couriers';
export const SERVICE_AREAS_COLLECTION = 'courierServiceAreas';
export const AUDIT_LOGS_COLLECTION = 'courierAuditLogs';

// Helper to get branches subcollection ref
export const getBranchesColRef = (courierId) =>
  collection(db, COURIERS_COLLECTION, courierId, 'branches');

// Helper to generate IDs
export const generateCourierId = () => {
  const rand = Math.floor(100 + Math.random() * 900);
  return `CR-${Date.now().toString().slice(-4)}${rand}`;
};

export const generateBranchId = () => {
  const rand = Math.floor(100 + Math.random() * 900);
  return `BR-${Date.now().toString().slice(-4)}${rand}`;
};

export const generateServiceAreaId = (courierId, branchId, stateCode, districtCode) => {
  const cleanState = (stateCode || 'IN').replace(/\s+/g, '_').toUpperCase();
  const cleanDist = (districtCode || 'GEN').replace(/\s+/g, '_').toUpperCase();
  return `${courierId}_${branchId}_${cleanState}_${cleanDist}`;
};

// ─────────────────────────────────────────────
// AUDIT LOGGER
// ─────────────────────────────────────────────
export const logCourierAudit = async ({
  actor = 'SUPERADMIN',
  action,
  entityType,
  entityId,
  previousValue = null,
  newValue = null,
  notes = ''
}) => {
  try {
    const logData = {
      actor,
      action,
      entityType, // 'COURIER' | 'BRANCH' | 'SERVICE_AREA' | 'DOCUMENT'
      entityId,
      previousValue: previousValue ? JSON.parse(JSON.stringify(previousValue)) : null,
      newValue: newValue ? JSON.parse(JSON.stringify(newValue)) : null,
      notes,
      timestamp: Timestamp.now(),
      createdAt: new Date().toISOString()
    };
    await addDoc(collection(db, AUDIT_LOGS_COLLECTION), logData);
  } catch (err) {
    console.warn('Could not write audit log to Firestore (may be offline or demo mode):', err);
  }
};

// ─────────────────────────────────────────────
// COURIER CRUD
// ─────────────────────────────────────────────
export const getCouriers = async () => {
  try {
    const q = query(collection(db, COURIERS_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const couriers = [];
    snapshot.forEach((docSnap) => {
      couriers.push({ ...docSnap.data(), courierId: docSnap.id });
    });
    return couriers;
  } catch (error) {
    console.warn('Firestore getCouriers failed, falling back:', error);
    return null;
  }
};

export const createCourier = async (courierData, actor = 'SUPERADMIN') => {
  const courierId = courierData.courierId || generateCourierId();
  const newCourier = {
    courierId,
    name: courierData.name || '',
    logo: courierData.logo || '',
    website: courierData.website || '',
    contactPerson: courierData.contactPerson || '',
    contactNumber: courierData.contactNumber || '',
    email: courierData.email || '',
    business: {
      registrationNumber: courierData.business?.registrationNumber || '',
      gstNumber: courierData.business?.gstNumber || '',
      pan: courierData.business?.pan || '',
      otherDetails: courierData.business?.otherDetails || ''
    },
    documents: courierData.documents || [],
    active: courierData.active !== undefined ? courierData.active : true,
    createdAt: Timestamp.now(),
    createdBy: actor,
    updatedAt: Timestamp.now(),
    updatedBy: actor
  };

  const docRef = doc(db, COURIERS_COLLECTION, courierId);
  await setDoc(docRef, newCourier);

  await logCourierAudit({
    actor,
    action: 'COURIER_CREATED',
    entityType: 'COURIER',
    entityId: courierId,
    newValue: newCourier,
    notes: `Created courier company ${newCourier.name}`
  });

  return newCourier;
};

export const updateCourier = async (courierId, updates, previousData, actor = 'SUPERADMIN') => {
  const docRef = doc(db, COURIERS_COLLECTION, courierId);
  const updatedPayload = {
    ...updates,
    updatedAt: Timestamp.now(),
    updatedBy: actor
  };

  await updateDoc(docRef, updatedPayload);

  await logCourierAudit({
    actor,
    action: 'COURIER_UPDATED',
    entityType: 'COURIER',
    entityId: courierId,
    previousValue: previousData,
    newValue: updatedPayload,
    notes: `Updated courier company details`
  });

  return updatedPayload;
};

export const toggleCourierStatus = async (courierId, currentStatus, courierName, actor = 'SUPERADMIN') => {
  const nextStatus = !currentStatus;
  const docRef = doc(db, COURIERS_COLLECTION, courierId);
  await updateDoc(docRef, {
    active: nextStatus,
    updatedAt: Timestamp.now(),
    updatedBy: actor
  });

  await logCourierAudit({
    actor,
    action: nextStatus ? 'COURIER_ACTIVATED' : 'COURIER_DEACTIVATED',
    entityType: 'COURIER',
    entityId: courierId,
    previousValue: { active: currentStatus },
    newValue: { active: nextStatus },
    notes: `${nextStatus ? 'Activated' : 'Deactivated'} courier company ${courierName}`
  });

  return nextStatus;
};

// ─────────────────────────────────────────────
// AUTHORIZED BRANCHES CRUD
// ─────────────────────────────────────────────
export const getBranchesForCourier = async (courierId) => {
  try {
    const branchesRef = getBranchesColRef(courierId);
    const q = query(branchesRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    const branches = [];
    snapshot.forEach((docSnap) => {
      branches.push({ ...docSnap.data(), branchId: docSnap.id });
    });
    return branches;
  } catch (error) {
    console.warn(`Firestore getBranchesForCourier(${courierId}) failed:`, error);
    return [];
  }
};

export const getAllBranchesAcrossCouriers = async (courierList = []) => {
  const allBranches = [];
  for (const c of courierList) {
    const branches = await getBranchesForCourier(c.courierId);
    branches.forEach((b) => {
      allBranches.push({
        ...b,
        courierId: c.courierId,
        courierName: c.name,
        courierActive: c.active
      });
    });
  }
  return allBranches;
};

export const createBranch = async (courierId, branchData, courierName = '', actor = 'SUPERADMIN') => {
  const branchId = branchData.branchId || generateBranchId();
  const newBranch = {
    branchId,
    courierId,
    branchCode: branchData.branchCode || `${courierId}-${branchId}`,
    branchName: branchData.branchName || '',
    contactPerson: branchData.contactPerson || '',
    contactNumber: branchData.contactNumber || '',
    alternateContactNumber: branchData.alternateContactNumber || '',
    email: branchData.email || '',
    address: {
      addressLine1: branchData.address?.addressLine1 || '',
      addressLine2: branchData.address?.addressLine2 || '',
      city: branchData.address?.city || '',
      stateCode: branchData.address?.stateCode || '',
      stateName: branchData.address?.stateName || '',
      districtCode: branchData.address?.districtCode || '',
      districtName: branchData.address?.districtName || '',
      pinCode: branchData.address?.pinCode || ''
    },
    documents: branchData.documents || [],
    active: branchData.active !== undefined ? branchData.active : true,
    createdAt: Timestamp.now(),
    createdBy: actor,
    updatedAt: Timestamp.now(),
    updatedBy: actor
  };

  const branchDocRef = doc(db, COURIERS_COLLECTION, courierId, 'branches', branchId);
  await setDoc(branchDocRef, newBranch);

  await logCourierAudit({
    actor,
    action: 'BRANCH_CREATED',
    entityType: 'BRANCH',
    entityId: branchId,
    newValue: newBranch,
    notes: `Created authorized branch "${newBranch.branchName}" under courier ${courierName || courierId}`
  });

  return newBranch;
};

export const updateBranch = async (courierId, branchId, updates, previousData, actor = 'SUPERADMIN') => {
  const branchDocRef = doc(db, COURIERS_COLLECTION, courierId, 'branches', branchId);
  const updatedPayload = {
    ...updates,
    updatedAt: Timestamp.now(),
    updatedBy: actor
  };

  await updateDoc(branchDocRef, updatedPayload);

  await logCourierAudit({
    actor,
    action: 'BRANCH_UPDATED',
    entityType: 'BRANCH',
    entityId: branchId,
    previousValue: previousData,
    newValue: updatedPayload,
    notes: `Updated branch details for ${branchId}`
  });

  return updatedPayload;
};

export const toggleBranchStatus = async (courierId, branchId, currentStatus, branchName, actor = 'SUPERADMIN') => {
  const nextStatus = !currentStatus;
  const branchDocRef = doc(db, COURIERS_COLLECTION, courierId, 'branches', branchId);
  await updateDoc(branchDocRef, {
    active: nextStatus,
    updatedAt: Timestamp.now(),
    updatedBy: actor
  });

  await logCourierAudit({
    actor,
    action: nextStatus ? 'BRANCH_ACTIVATED' : 'BRANCH_DEACTIVATED',
    entityType: 'BRANCH',
    entityId: branchId,
    previousValue: { active: currentStatus },
    newValue: { active: nextStatus },
    notes: `${nextStatus ? 'Activated' : 'Deactivated'} authorized branch ${branchName}`
  });

  return nextStatus;
};

// ─────────────────────────────────────────────
// SERVICE AREA ASSIGNMENT (MANY-TO-MANY)
// ─────────────────────────────────────────────
/**
 * Normalizes a serviceArea record ensuring stateName, stateCode, districtName, and districtCode
 * are present even if the stored document was created by legacy code without those fields.
 */
export const normalizeServiceArea = (id, data = {}) => {
  let stateName = data.stateName || data.stateCode || '';
  let stateCode = data.stateCode || data.stateName || '';
  let districtName = data.districtName || data.districtCode || '';
  let districtCode = data.districtCode || data.districtName || '';

  // If stateName or districtName is missing, recover from ID
  // ID format: ${courierId}_${branchId}_${cleanState}_${cleanDist}
  if (!stateName || !districtName) {
    const parts = (id || '').split('_');
    if (parts.length >= 4) {
      const idState = parts[2];
      const idDist = parts.slice(3).join('_');

      if (!stateName && idState) {
        const matchedState = (stateDistrictData?.states || []).find(
          (s) => s.state.replace(/\s+/g, '_').toUpperCase() === idState.toUpperCase()
        );
        stateName = matchedState ? matchedState.state : idState;
        stateCode = stateName;
      }

      if (!districtName && idDist) {
        districtName = idDist;
        districtCode = idDist;
      }
    }
  }

  // Canonicalize districtName casing from stateDistrictData if possible
  if (stateName && districtName) {
    const matchedState = (stateDistrictData?.states || []).find(
      (s) => s.state.toLowerCase() === stateName.toLowerCase()
    );
    if (matchedState) {
      const matchedDist = (matchedState.districts || []).find(
        (d) =>
          d.toLowerCase() === districtName.toLowerCase() ||
          d.replace(/\s+/g, '_').toLowerCase() === districtName.toLowerCase()
      );
      if (matchedDist) {
        districtName = matchedDist;
        districtCode = matchedDist;
      }
    }
  }

  return {
    ...data,
    serviceAreaId: id,
    courierId: data.courierId || (id ? id.split('_')[0] : ''),
    branchId: data.branchId || (id ? id.split('_')[1] : ''),
    stateName,
    stateCode,
    districtName,
    districtCode,
    active: data.active !== false
  };
};

/**
 * Self-heals any legacy service area records in Firestore by backfilling
 * missing stateName, stateCode, and districtName fields.
 */
export const repairServiceAreasInFirestore = async () => {
  try {
    const q = query(collection(db, SERVICE_AREAS_COLLECTION));
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);
    let needUpdateCount = 0;

    snapshot.forEach((d) => {
      const data = d.data();
      const id = d.id;
      if (!data.stateName || !data.districtName || !data.stateCode) {
        const norm = normalizeServiceArea(id, data);
        if (norm.stateName && norm.districtName) {
          batch.update(d.ref, {
            stateName: norm.stateName,
            stateCode: norm.stateCode || norm.stateName,
            districtName: norm.districtName,
            districtCode: norm.districtCode || norm.districtName
          });
          needUpdateCount++;
        }
      }
    });

    if (needUpdateCount > 0) {
      await batch.commit();
      console.log(`[CourierService] Repaired ${needUpdateCount} service areas with state/district in Firestore.`);
    }
  } catch (err) {
    console.warn('[CourierService] Error repairing service areas:', err);
  }
};

export const getAllServiceAreas = async () => {
  try {
    const q = query(collection(db, SERVICE_AREAS_COLLECTION));
    const snapshot = await getDocs(q);
    const list = [];
    snapshot.forEach((d) => {
      list.push(normalizeServiceArea(d.id, d.data()));
    });
    return list;
  } catch (error) {
    console.warn('Firestore getAllServiceAreas failed:', error);
    return null;
  }
};

export const getServiceAreasForBranch = async (branchId) => {
  try {
    const q = query(
      collection(db, SERVICE_AREAS_COLLECTION),
      where('branchId', '==', branchId)
    );
    const snapshot = await getDocs(q);
    const list = [];
    snapshot.forEach((d) => {
      list.push(normalizeServiceArea(d.id, d.data()));
    });
    return list;
  } catch (err) {
    console.warn(`getServiceAreasForBranch(${branchId}) error:`, err);
    return [];
  }
};

export const assignBranchDistricts = async ({
  courierId,
  branchId,
  stateCode,
  stateName,
  districtsToAssign = [], // Array of { districtCode, districtName }
  actor = 'SUPERADMIN'
}) => {
  const batch = writeBatch(db);
  const assignedRecords = [];

  const stName = stateName || stateCode || 'Odisha';
  const stCode = stateCode || stateName || 'Odisha';

  for (const item of districtsToAssign) {
    const distCode = item.districtCode || item.districtName;
    const distName = item.districtName || item.districtCode || distCode;
    const saId = generateServiceAreaId(courierId, branchId, stCode, distCode);
    const docRef = doc(db, SERVICE_AREAS_COLLECTION, saId);

    const data = {
      courierId,
      branchId,
      stateCode: stCode,
      stateName: stName,
      districtCode: distCode,
      districtName: distName,
      active: true,
      assignedAt: Timestamp.now(),
      assignedBy: actor
    };

    batch.set(docRef, data, { merge: true });
    assignedRecords.push({ ...data, serviceAreaId: saId });
  }

  await batch.commit();

  await logCourierAudit({
    actor,
    action: 'DISTRICTS_ASSIGNED',
    entityType: 'SERVICE_AREA',
    entityId: branchId,
    newValue: {
      courierId,
      branchId,
      state: stName,
      districtsCount: districtsToAssign.length,
      districts: districtsToAssign.map(d => d.districtName || d.districtCode)
    },
    notes: `Assigned ${districtsToAssign.length} districts in ${stName} to branch ${branchId}`
  });

  return assignedRecords;
};

export const removeBranchDistricts = async ({
  courierId,
  branchId,
  stateCode,
  districtCodesToRemove = [],
  actor = 'SUPERADMIN'
}) => {
  const batch = writeBatch(db);

  for (const distCode of districtCodesToRemove) {
    const saId = generateServiceAreaId(courierId, branchId, stateCode, distCode);
    const docRef = doc(db, SERVICE_AREAS_COLLECTION, saId);
    batch.delete(docRef);
  }

  await batch.commit();

  await logCourierAudit({
    actor,
    action: 'DISTRICTS_REMOVED',
    entityType: 'SERVICE_AREA',
    entityId: branchId,
    previousValue: {
      courierId,
      branchId,
      removedCount: districtCodesToRemove.length,
      districts: districtCodesToRemove
    },
    notes: `Removed ${districtCodesToRemove.length} districts from branch ${branchId}`
  });
};

// ─────────────────────────────────────────────
// ORDER ASSIGNMENT RESOLUTION (SECTIONS 15-18)
// ─────────────────────────────────────────────
export const resolveEligibleCourierBranchesForDistrict = async ({
  districtName,
  stateName = '',
  couriersCache = null,
  branchesCache = null,
  serviceAreasCache = null
}) => {
  const normalizedDist = (districtName || '').trim().toLowerCase();
  const normalizedState = (stateName || '').trim().toLowerCase();

  // 1. Fetch active service areas covering this district
  let activeServiceAreas = [];
  if (serviceAreasCache) {
    activeServiceAreas = serviceAreasCache.filter((sa) => {
      if (sa.active === false) return false;
      const saDist = (sa.districtName || sa.districtCode || '').trim().toLowerCase();
      const distMatches =
        saDist === normalizedDist ||
        saDist.replace(/\s+/g, '_') === normalizedDist.replace(/\s+/g, '_');
      if (!distMatches) return false;

      if (normalizedState && (sa.stateName || sa.stateCode)) {
        const saState = (sa.stateName || sa.stateCode || '').trim().toLowerCase();
        return (
          saState === normalizedState ||
          saState.replace(/\s+/g, '_') === normalizedState.replace(/\s+/g, '_')
        );
      }
      return true;
    });
  } else {
    try {
      const q = query(
        collection(db, SERVICE_AREAS_COLLECTION),
        where('active', '==', true)
      );
      const snapshot = await getDocs(q);
      snapshot.forEach((d) => {
        const norm = normalizeServiceArea(d.id, d.data());
        const saDist = (norm.districtName || norm.districtCode || '').trim().toLowerCase();
        const distMatches =
          saDist === normalizedDist ||
          saDist.replace(/\s+/g, '_') === normalizedDist.replace(/\s+/g, '_');
        if (distMatches) {
          if (
            !normalizedState ||
            !norm.stateName ||
            norm.stateName.toLowerCase() === normalizedState ||
            norm.stateCode?.toLowerCase() === normalizedState
          ) {
            activeServiceAreas.push(norm);
          }
        }
      });
    } catch (err) {
      console.warn('Error querying service areas:', err);
    }
  }

  const eligibleOptions = [];

  // 2. For each active service area, lookup the current branch and courier
  // (State/district info comes from branch.address, not duplicated in serviceArea)
  for (const sa of activeServiceAreas) {
    let branch = null;
    let courier = null;

    if (branchesCache) {
      branch = branchesCache.find((b) => b.branchId === sa.branchId);
    } else {
      try {
        const branchSnap = await getDoc(
          doc(db, COURIERS_COLLECTION, sa.courierId, 'branches', sa.branchId)
        );
        if (branchSnap.exists()) {
          branch = { ...branchSnap.data(), branchId: branchSnap.id };
        }
      } catch (err) {
        console.warn(`Could not fetch branch ${sa.branchId}:`, err);
      }
    }

    // Branch must exist and be active
    if (!branch || branch.active === false) continue;

    if (couriersCache) {
      courier = couriersCache.find((c) => c.courierId === sa.courierId);
    } else {
      try {
        const courierSnap = await getDoc(doc(db, COURIERS_COLLECTION, sa.courierId));
        if (courierSnap.exists()) {
          courier = { ...courierSnap.data(), courierId: courierSnap.id };
        }
      } catch (err) {
        console.warn(`Could not fetch courier ${sa.courierId}:`, err);
      }
    }

    // Courier must exist and be active
    if (!courier || courier.active === false) continue;

    // Contact number is directly fetched from current branch document (prevents stale info)
    eligibleOptions.push({
      courierId: courier.courierId,
      courierName: courier.name,
      courierLogo: courier.logo,
      courierWebsite: courier.website,
      branchId: branch.branchId,
      branchCode: branch.branchCode,
      branchName: branch.branchName,
      contactPerson: branch.contactPerson,
      contactNumber: branch.contactNumber, // Direct current branch contact
      alternateContactNumber: branch.alternateContactNumber || '',
      email: branch.email || '',
      address: branch.address, // State/district from branch address (always fresh)
      serviceAreaId: sa.serviceAreaId,
      districtCode: sa.districtCode // Only this from optimized serviceArea
    });
  }

  return eligibleOptions;
};

// Generates the immutable snapshot for an assigned order (Section 18)
export const createOrderCourierAssignmentSnapshot = (option, actor = 'SUPERADMIN') => {
  return {
    courierAssignment: {
      courierId: option.courierId,
      courierName: option.courierName,
      branchId: option.branchId,
      branchName: option.branchName,
      branchCode: option.branchCode,
      contactPerson: option.contactPerson,
      contactNumber: option.contactNumber, // Snapshotted at assignment time
      alternateContactNumber: option.alternateContactNumber || '',
      email: option.email || '',
      assignedAt: Timestamp.now(),
      assignedBy: actor,
      assignedAtIso: new Date().toISOString()
    }
  };
};

// ─────────────────────────────────────────────
// AUDIT LOGS RETRIEVAL
// ─────────────────────────────────────────────
export const getAuditLogs = async (limitCount = 50) => {
  try {
    const q = query(
      collection(db, AUDIT_LOGS_COLLECTION),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);
    const logs = [];
    snapshot.forEach((d) => {
      logs.push({ ...d.data(), logId: d.id });
    });
    return logs;
  } catch (err) {
    console.warn('Firestore getAuditLogs failed:', err);
    return [];
  }
};
